import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { StatusLED } from "@/components/infra";
import { DCYB_PLATFORM, openInvestorDeck } from "@/lib/dcybStock";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0FBF77";
const RED = "#E5484D";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const PlatformCard = () => (
  <button
    type="button"
    onClick={openInvestorDeck}
    data-testid="market-card-dcyb"
    aria-label={`DCYB ${DCYB_PLATFORM.status} — request Investor Deck access`}
    className="group flex items-center gap-4 px-5 py-4 min-w-[230px] max-w-full shrink-0 text-left bg-white hover:bg-[#F7F8F9] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[#0F141B]"
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1.5">
        <Logo src={DCYB_PLATFORM.mark} testId="market-card-dcyb-logo" />
        <span data-testid="market-card-dcyb-symbol" className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#0F141B] whitespace-nowrap">
          {DCYB_PLATFORM.symbol}
        </span>
      </div>
      <div data-testid="market-card-dcyb-ownership" className="font-display text-lg font-extrabold tracking-tight text-[#0F141B] leading-none mb-1">
        {DCYB_PLATFORM.ownership}
      </div>
      <div data-testid="market-card-dcyb-stage" className="font-mono-label text-[10.5px] text-[#3C4450]">{DCYB_PLATFORM.stage}</div>
      <span data-testid="market-card-dcyb-access" className="inline-flex items-center gap-1 mt-1 font-mono-label text-[10.5px] text-[#078552]">
        Get Early Access
        <ArrowUpRight size={11} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </button>
);

const PlatformBoardRow = () => (
  <button
    type="button"
    onClick={openInvestorDeck}
    data-testid="full-board-dcyb"
    aria-label={`DCYB ${DCYB_PLATFORM.status} — request Investor Deck access`}
    className="group col-span-2 flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 border-b border-r border-[#D2D5DA] bg-white hover:bg-[#F7F8F9] text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[#0F141B]"
  >
    <span data-testid="full-board-dcyb-symbol" className="inline-flex items-center gap-2 font-mono-label text-[10px] tracking-[0.12em] text-[#0F141B]">
      <Logo src={DCYB_PLATFORM.mark} size={16} boxed={false} testId="full-board-dcyb-logo" />
      {DCYB_PLATFORM.symbol}
    </span>
    <span data-testid="full-board-dcyb-ownership" className="font-mono-label text-[10.5px] text-[#3C4450]">{DCYB_PLATFORM.ownership}</span>
    <span data-testid="full-board-dcyb-stage" className="font-mono-label text-[10.5px] text-[#3C4450]">{DCYB_PLATFORM.stage}</span>
    <span data-testid="full-board-dcyb-access" className="inline-flex items-center gap-1 font-mono-label text-[10px] text-[#078552]">
      Early Access
      <ArrowUpRight size={10} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </span>
  </button>
);

const Logo = ({ domain, src, size = 20, boxed = true, testId }) => (
  <span
    aria-hidden
    className={`inline-flex items-center justify-center shrink-0 overflow-hidden ${
      boxed
        ? "w-7 h-7 rounded-md bg-white border border-[#DADCE1] shadow-[inset_0_1px_0_rgba(255,255,255,1),0_1px_1px_rgba(15,17,23,0.04)]"
        : ""
    }`}
  >
    <img
      src={src || `https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
      data-testid={testId}
      alt=""
      loading="lazy"
      style={{ width: size * 0.7, height: size * 0.7 }}
      className="object-contain"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  </span>
);

const Sparkline = ({ q, w = 96, h = 36 }) => {
  const values = q.spark && q.spark.length > 1 ? q.spark : [q.prev_close, q.price];
  const all = [...values, q.prev_close];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const x = (i) => (i / (values.length - 1)) * w;
  const y = (v) => h - 3 - ((v - min) / range) * (h - 6);
  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const up = q.change_pct >= 0;
  const color = up ? GREEN : RED;
  const gid = `sg-${slug(q.symbol)}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line
        x1="0"
        x2={w}
        y1={y(q.prev_close)}
        y2={y(q.prev_close)}
        stroke="#8A94A2"
        strokeWidth="1"
        strokeDasharray="2 3"
        opacity="0.6"
      />
      <polygon
        points={`0,${h} ${pts} ${w},${h}`}
        fill={`url(#${gid})`}
      />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
      <circle
        cx={x(values.length - 1)}
        cy={y(values[values.length - 1])}
        r="2"
        fill={color}
      />
    </svg>
  );
};

const fmt = (n) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Change = ({ q, badge }) => {
  const up = q.change_pct >= 0;
  const color = up ? GREEN : RED;
  const text = `${up ? "+" : ""}${fmt(q.change)} (${up ? "+" : ""}${q.change_pct.toFixed(2)}%)`;
  if (badge) {
    return (
      <span
        className="font-mono-label text-[10.5px] px-1.5 py-0.5"
        style={{ color, backgroundColor: up ? "rgba(15,191,119,0.12)" : "rgba(229,72,77,0.10)" }}
      >
        {text}
      </span>
    );
  }
  return (
    <span className="font-mono-label text-[10.5px]" style={{ color }}>
      {text}
    </span>
  );
};

export const MarketBoard = () => {
  const [market, setMarket] = useState(null);
  const marketReady = Boolean(market);

  useEffect(() => {
    if (!marketReady || window.location.hash !== "#market") return;
    let cancelled = false;
    // Initial cross-page anchors must wait for the asynchronous board and fonts.
    // Depend on readiness, not each refreshed snapshot, to preserve user scrolling.
    document.fonts.ready.then(() => {
      if (!cancelled && window.location.hash === "#market") {
        document.getElementById("market")?.scrollIntoView({ behavior: "instant", block: "start" });
      }
    });
    return () => { cancelled = true; };
  }, [marketReady]);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/api/stocks/market`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => alive && d && setMarket(d))
        .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!market) {
    return (
      <section id="market" data-testid="market-board" className="border-b hairline bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="font-mono-label text-[11px] text-[#0F141B] flex items-center gap-3">
              <StatusLED />
              / DCYB Markets · Live
            </div>
            <span className="font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#8A94A2]">
              Loading quotes…
            </span>
          </div>
          <div
            data-testid="market-board-loading"
            className="flex gap-0 overflow-hidden border border-[#D2D5DA] divide-x divide-[#D2D5DA]"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-1 min-w-[180px] px-5 py-4">
                <div className="h-3 w-20 bg-[#EDEFF2] animate-pulse mb-2.5" />
                <div className="h-5 w-24 bg-[#EDEFF2] animate-pulse mb-2.5" />
                <div className="h-3 w-16 bg-[#EDEFF2] animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const trending = market.digital_infra
    .filter((q) => q.change_pct > 0)
    .sort((a, b) => b.change_pct - a.change_pct)
    .slice(0, 5);

  return (
    <section id="market" data-testid="market-board" className="border-b hairline bg-white scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="font-mono-label text-[11px] text-[#0F141B] flex items-center gap-3">
            <StatusLED />
            / DCYB Markets · Live
          </div>
          <span className="font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#8A94A2]">
            Delayed quotes · Refreshes every 5 min
          </span>
        </div>

        {/* Markets strip */}
        <div
          data-testid="market-cards-strip"
          className="flex gap-0 overflow-x-auto scrollbar-none border border-[#D2D5DA] divide-x divide-[#D2D5DA] mb-10"
        >
          <PlatformCard />
          {market.markets.map((q) => (
            <div
              key={q.symbol}
              data-testid={`market-card-${slug(q.symbol)}`}
              className="flex items-center gap-4 px-5 py-4 min-w-[230px] shrink-0 bg-white hover:bg-[#F7F8F9] transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <Logo domain={q.domain} />
                  <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#0F141B] whitespace-nowrap">
                    {q.name}
                  </span>
                </div>
                <div className="font-display text-lg font-extrabold tracking-tight text-[#0F141B] leading-none mb-1">
                  {fmt(q.price)}
                </div>
                <Change q={q} />
              </div>
              <Sparkline q={q} w={84} h={38} />
            </div>
          ))}
        </div>

        {/* Trending digital infra */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12">
          <div>
            <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-[#0F141B] mb-4">
              Trending Up — <span className="text-laser">Digital Infra.</span>
            </h3>
            <div data-testid="trending-list" className="border-t border-[#D2D5DA]">
              {trending.length === 0 && (
                <p className="py-4 text-[13px] text-[#8A94A2]">
                  No gainers on the board right now — check back after the next refresh.
                </p>
              )}
              {trending.map((q) => (
                <div
                  key={q.symbol}
                  data-testid={`trending-row-${slug(q.symbol)}`}
                  className="grid grid-cols-12 items-center gap-3 py-3 border-b border-[#D2D5DA]"
                >
                  <div className="col-span-4 sm:col-span-4 flex items-center gap-2.5">
                    <Logo domain={q.domain} />
                    <div className="min-w-0">
                      <div className="font-display text-[15px] font-extrabold tracking-tight text-[#0F141B] leading-tight">
                        {q.symbol}
                      </div>
                      <div className="text-[12px] text-[#8A94A2] truncate">{q.name}</div>
                    </div>
                  </div>
                  <div className="col-span-3 flex justify-center">
                    <Sparkline q={q} w={80} h={32} />
                  </div>
                  <div className="col-span-5 text-right">
                    <div className="font-display text-[15px] font-extrabold text-[#0F141B] mb-0.5">
                      {fmt(q.price)}
                    </div>
                    <Change q={q} badge />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hidden lg:block">
            <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-[#0F141B] mb-4">
              The Full <span className="text-chrome">Board.</span>
            </h3>
            <div data-testid="full-board-grid" className="grid grid-cols-2 border-t border-l border-[#D2D5DA]">
              <PlatformBoardRow />
              {market.digital_infra.map((q) => (
                <div
                  key={q.symbol}
                  className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-r border-[#D2D5DA]"
                >
                  <span className="inline-flex items-center gap-2 font-mono-label text-[10px] tracking-[0.12em] text-[#0F141B]">
                    <Logo domain={q.domain} size={16} boxed={false} />
                    {q.symbol}
                  </span>
                  <span className="font-mono-label text-[10.5px] text-[#3C4450]">{fmt(q.price)}</span>
                  <span
                    className="font-mono-label text-[10px]"
                    style={{ color: q.change_pct >= 0 ? GREEN : RED }}
                  >
                    {q.change_pct >= 0 ? "+" : ""}
                    {q.change_pct.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-3 font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#8A94A2]">
              The companies powering the buildout — chips, power, cooling, REITs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketBoard;
